import {
  Box,
  Typography,
  Button,
  AppBar,
  CardMedia,
  Divider,
  IconButton,
  Grid,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  ApplicationProps,
  Broker,
  BrokerList,
  Document,
  esignApplicationResponse,
} from '../../redux-store/types/api-types';
import {
  esignApplication,
  getApplicationDetailsWithRefId,
} from '../../redux-store/actions/onBoarding';
import { useHistory } from 'react-router';
import { RootStateType } from '../../redux-store/reducers';
import { Footer } from '../commonComponents';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ESIGN_STATUS } from '../../utils/constant';
import {
  acknowledgeApplication,
  getBrokers,
  getDocuments,
  isFormValidForSubmission,
} from '../../redux-store/actions/application';
import { showError } from '../../redux-store/actions/auth';
import { ApplicationDetailsCommonLayout } from '../ApplicationDetails/index';
import { getNationalityList } from '../../redux-store/actions';
import { nationaliyType } from '../../redux-store/types/mdms';
import { nomineeDeclaration } from '../declaration';
import { useFeatureToggles } from '../../utils/useDataMdms';
import { checkValidationBasedOnDate } from '../../utils/utilityFunctions';
import { Formik } from 'formik';
import MFCheckbox from '../../lib/formik/Checkbox';

export const Item = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  paddingTop: '10px',
  marginLeft: '2px',
  color: theme.palette.text.secondary,
  fontSize: '15px',
  fontWeight: 500,
  letterSpacing: '0.5px',
}));
export const ItemDetails = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  fontSize: '16px',
  marginLeft: '2px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
}));
export const Gridstyles = styled('div')(({ theme }) => ({
  paddingLeft: '10%',
  [theme.breakpoints.only('xs')]: {
    paddingLeft: 0,
  },
}));
export type Values = {
  nomineeDeclaration: boolean | null;
};

const initialValues: Values = {
  nomineeDeclaration: false,
};

export default function InvestorApplication(): JSX.Element {
  const { referenceId } = useParams<{ referenceId: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const { investor, auth } = useSelector((store: RootStateType) => store);
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState<ApplicationProps>();
  const applicationDetailRef = useRef<HTMLDivElement>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [declaration, setDeclaration] = useState(initialValues);
  const _nomineeDeclaration = nomineeDeclaration();
  const { toggler } = useFeatureToggles();

  useEffect(() => {
    (async function () {
      try {
        const _application = (await dispatch(
          getApplicationDetailsWithRefId(referenceId)
        )) as unknown as ApplicationProps;
        setApplication(_application);
        setDeclaration({
          nomineeDeclaration: _application?.nomineeDeclaration || false,
        });
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
    setLoading(true);
  }, []);

  useEffect(() => {
    const { token } = investor;
    if (!token) {
      history.push(`/investment-details/${referenceId}/details`);
    }
  }, []);

  useEffect(() => {
    document.title = 'DEZERV :: Application Details';
  }, []);

  const handleEsign = async (values: Values) => {
    try {
      setIsSubmitting(true);
      const nationalitiesMdmsMasters = (await dispatch(
        getNationalityList()
      )) as unknown as nationaliyType;
      const documentsResponse = (await dispatch(getDocuments())) as unknown as Document;
      const brokersMasters = (await dispatch(getBrokers())) as unknown as BrokerList;
      if (
        checkValidationBasedOnDate(
          application?.createdAt || '',
          toggler?.dateFeatureToggles?.nomineeRemovalAndDeclarationValidationDate || ''
        ) &&
        values.nomineeDeclaration === false
      ) {
        throw 'Declaration is required';
      }
      if (ESIGN_STATUS.PENDING_SIGNATURE !== application?.signDetails.status) {
        ESIGN_STATUS.NOT_GENERATED === application?.signDetails.status
          ? await isFormValidForSubmission(
              application as ApplicationProps,
              true,
              true,
              nationalitiesMdmsMasters,
              brokersMasters.broker_list as Broker[],
              documentsResponse,
              auth.role,
              toggler
            )
          : await isFormValidForSubmission(
              application as ApplicationProps,
              true,
              false,
              nationalitiesMdmsMasters,
              brokersMasters.broker_list as Broker[],
              documentsResponse,
              auth.role,
              toggler
            );
      }
      const { signDetails, id } = application || {};
      if (signDetails?.url) {
        window.open(signDetails?.url, '_blank');
        return;
      }
      if (id) {
        const applicationResponse = (checkValidationBasedOnDate(
          application?.createdAt || '',
          toggler?.dateFeatureToggles?.nomineeRemovalAndDeclarationValidationDate || ''
        )
          ? await dispatch(
              acknowledgeApplication({
                body: {
                  ...application,
                  nomineeDeclaration: values.nomineeDeclaration,
                },
                applicationId: id,
              })
            )
          : application) as unknown as ApplicationProps;
        if (
          applicationResponse.nomineeDeclaration ||
          !checkValidationBasedOnDate(
            application?.createdAt || '',
            toggler?.dateFeatureToggles?.nomineeRemovalAndDeclarationValidationDate || ''
          )
        ) {
          const response = (await dispatch(
            esignApplication(referenceId)
          )) as unknown as esignApplicationResponse;
          setApplication({ ...application, signDetails: response } as unknown as ApplicationProps);
          window.open(response?.url, '_blank');
        }
      }
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
      console.error((e as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik initialValues={declaration} onSubmit={handleEsign} enableReinitialize={true}>
      {({ handleSubmit, values, setValues }) => (
        <Box
          sx={{
            bgcolor: { xs: '', sm: 'rgba(238, 244, 251, 0.5)' },
          }}
          component="form"
          onSubmit={handleSubmit}>
          <IconButton
            sx={{
              position: 'fixed',
              right: 0,
              bottom: 75,
              borderRadius: '5px  0 0 5px',
              '&,:hover': {
                bgcolor: 'primary.main',
              },
            }}
            aria-label="Back To Top"
            onClick={() => applicationDetailRef.current?.scrollIntoView({ behavior: 'smooth' })}>
            <ArrowUpwardIcon fontSize="large" sx={{ color: 'common.white' }} />
          </IconButton>
          <AppBar position="fixed" elevation={0} sx={{ bgcolor: 'common.white' }}>
            <Box>
              <CardMedia
                component="img"
                src="/images/dezerv-logo.png"
                alt="Logo"
                sx={{ width: 'unset', m: 3 }}
              />
            </Box>
          </AppBar>
          <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
          <Box sx={{ py: 5, px: { xs: 5, sm: 10 } }} ref={applicationDetailRef}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 2,
                justifyContent: 'space-between',
                mt: 10,
              }}>
              <Typography
                component="h1"
                sx={{ fontSize: 20, fontWeight: 600, color: 'primary.main', mb: 4 }}>
                Application Details
              </Typography>
              {application?.editable && (
                <IconButton disabled={isSubmitting}>
                  <CardMedia
                    component="img"
                    src="/images/edit-icon-outlined.svg"
                    alt="Edit Icon"
                    sx={{ width: 'unset' }}
                    onClick={() =>
                      history.push(
                        `/investment-details/${referenceId}/investor-edit-application/distributor-details`,
                        {
                          id: application?.id,
                          applicant1ReferenceId: application?.applicant1ReferenceId,
                        }
                      )
                    }
                  />
                </IconButton>
              )}
            </Box>
            {application && (
              <>
                <ApplicationDetailsCommonLayout
                  loading={loading}
                  application={application as ApplicationProps}
                />
                <Grid item xs={12} mt={5}>
                  {checkValidationBasedOnDate(
                    application?.createdAt || '',
                    toggler?.dateFeatureToggles?.nomineeRemovalAndDeclarationValidationDate || ''
                  ) && (
                    <>
                      {_nomineeDeclaration}
                      <MFCheckbox
                        name={'nomineeDeclaration'}
                        label={`I confirm that I have read, understood, and agree to the above, regarding nomination for AIF units`}
                        disabled={application?.nomineeDeclaration as boolean}
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          textAlign: 'justify',
                          color: 'rgba(0,0,0,0.7)',
                          alignItems: 'flex-start',
                          '.MuiTypography-root': {
                            fontSize: 15,
                            fontWeight: 500,
                          },
                        }}
                        onChange={({ target: { checked } }) => {
                          setValues({
                            ...values,
                            nomineeDeclaration: checked,
                          });
                        }}
                      />
                    </>
                  )}
                </Grid>
                {![ESIGN_STATUS.SIGNED, ESIGN_STATUS.EXPIRED].includes(
                  application.signDetails.status
                ) && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      type="submit"
                      sx={{
                        color: 'common.white',
                        minWidth: '200px',
                        mt: 4,
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                      //    onClick={handleEsign}
                      disabled={isSubmitting}>
                      {`e-Sign ${isSubmitting ? '...' : ''}`}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
          <Footer />
        </Box>
      )}
    </Formik>
  );
}
